import * as React from 'react';
import {SystemIcons} from '../icons';
import {COLORS} from '../styles';
import {Size, States, Testable} from '../types';
import {InputFieldStyling, InputWrapper, NoteMessage, ValidationMessage} from './styling';
import {useFocusVisibleRef} from '../common';
import {TextFieldNote} from './types';
import TooltipOverflow from '../Tooltips/TooltipOverflow';
import { useTheme } from 'styled-components';

export type TextFieldProps =
  Testable & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'disabled' | 'id'  | 'type' | 'readOnly' | 'autoComplete' | 'pattern' | 'maxLength'>
  & {
  /**
     * Optional. The ID of the text field.
     */
  id?: string;

  /**
    * Optional. A boolean indicating whether the text field is disabled.
    */
  disabled?: boolean;

  /**
    * Optional. A boolean indicating whether the text field is read-only.
    */
  readOnly?: boolean;

  /**
    * Optional. A function to be called when the text in the text field changes.
    * It should take a string representing the new text.
    */
  onChange?: (text: string) => void;

  /**
    * Optional. The current value of the text field.
    */
  value?: string;

  /**
    * Optional. The validation message to be displayed when the text field is in an error state.
    */
  validationMessage?: string;

  /**
    * Optional. The type of the text field. Can be any valid HTML input type.
    */
  type?: string;

  /**
    * Optional. The state of the text field. Can be 'Invalid' or 'Valid'.
    */
  state?: States.Invalid | States.Valid;

  /**
    * Optional. The autocomplete attribute of the text field. Can be any valid HTML autocomplete value.
    */
  autoComplete?: string;

  /**
    * Optional. The placeholder text to be displayed in the text field when it is empty.
    */
  placeholder?: string;

  /**
    * Optional. A boolean indicating whether the text field is required.
    */
  required?: boolean;

  /**
    * Optional. The pattern attribute of the text field. Can be any valid HTML pattern value.
    */
  pattern?: string;

  /**
    * Optional. The maximum length of the text that can be entered in the text field.
    */
  maxLength?: number;

  /**
    * Optional. The position of the overflow tooltip. Can be 'top' or 'bottom'.
    */
  overflowTooltipPosition?: 'top' | 'bottom';

  /**
    * Optional. The size of the text field. Can be 'Small' or 'Medium'.
    */
  size?: Size.Small | Size.Medium;

  /**
    * Optional. The margin of the text field. Can be any valid CSS margin value.
    */
  margin?: string;

  /**
    * Optional. A note to be displayed below the text field.
    */
  note?: TextFieldNote;
};

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(({
                                      id,
                                      disabled,
                                      readOnly,
                                      onChange,
                                      value,
                                      validationMessage,
                                      type,
                                      autoComplete,
                                      placeholder,
                                      required,
                                      pattern,
                                      maxLength,
                                      state,
                                      size,
                                      margin,
                                      overflowTooltipPosition,
                                      note,
                                      className,
                                      dataTestId,
                                      ...rest
                                    }: TextFieldProps, ref) => {
  const elementRef = useFocusVisibleRef();
  const theme = useTheme();

  React.useImperativeHandle(ref, () => elementRef.current, [elementRef]);

  const cls = `${state || (validationMessage ? States.Invalid : '')} ${size || ''}`;

  return (
    <>
      <InputWrapper
        $readOnly={readOnly}
        $disabled={disabled}
        $margin={margin}
        onClick={(e: any) => {
          if (disabled || readOnly) {
            e.preventDefault();
          }
        }}>
        <InputFieldStyling
          id={id}
          data-testid={dataTestId}
          ref={elementRef}
          type={type || 'text'}
          value={value ?? ''}
          className={cls}
          tabIndex={readOnly || disabled ? -1 : 0}
          autoComplete={autoComplete}
          placeholder={disabled ? undefined : placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(e: any) => onChange && onChange(e?.target?.value || '')}
          $activeErrorMessage={!!validationMessage && state !== States.Valid}
          required={required}
          pattern={pattern}
          maxLength={maxLength}
          {...rest}
        />
        <TooltipOverflow value={value} position={overflowTooltipPosition} input={elementRef} withArrow={true} maxWidth="100%" size={size} align='end'>
          {value}
        </TooltipOverflow>

        {validationMessage && (
          <ValidationMessage className={size || ''} type={state ?? States.Invalid}>
            {state === States.Valid ? <SystemIcons.CheckMark color={COLORS.generateToken({componentType:'text', defaultVariant:'positive'}, theme)}/> :
              <SystemIcons.TechnicalWarning color={COLORS.generateToken({componentType:'text', defaultVariant:'critical'}, theme)}/>}
            <span>{validationMessage}</span>
          </ValidationMessage>
        )}
        {note && !disabled && (
          <NoteMessage className={size}>
            {note.icon}
            <span>{note.message}</span>
          </NoteMessage>
        )}
      </InputWrapper>
    </>
  );
});

export default TextField;
